/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { NodeResizer, Handle, Position, useStore } from '@xyflow/react'
import { Flex, Text, Box, Image } from '@chakra-ui/react'
import { IEmployee } from '../../../../../../interfaces'
import { memo, useState } from 'react'
import { iPosicaoTopo, medirTopo, posicionarTopos } from './BuildReactFlowGraph'

const ALTURA_CONECTOR = 24
const METADE_DO_CONECTOR = `${ALTURA_CONECTOR / 2}px`

const CustomNode: React.FC<any> = ({
  data,
  isConnectable
}: {
  data: {
    label: string
    colaboradores: IEmployee[]
    cor?: string
    cores: string[]
    nivel: number
    departamentoId: string
    posicoes?: Record<string, iPosicaoTopo>
    onMoverTopo?: (squadId: string, colaboradorId: string, posicao: iPosicaoTopo) => void
  }
  isConnectable: boolean
}) => {
  // A alça continua montada porque a ligação precisa dela para ser desenhada.
  const visibilidadeAlca = isConnectable ? 'visible' : 'hidden'
  const zoom = useStore((s) => s.transform[2])
  const [Arraste, setArraste] = useState<{ id: string } & iPosicaoTopo>()
  const posicoesPadrao = data.onMoverTopo ? posicionarTopos(data.colaboradores ?? []) : {}

  function posicaoDe(id: string): iPosicaoTopo {
    return data.posicoes?.[id] ?? posicoesPadrao[id] ?? { x: 0, y: 0 }
  }

  // só o líder da cadeia é arrastado, os subordinados vão junto com ele
  function iniciarArraste(e: React.PointerEvent, id: string): void {
    e.preventDefault()
    const origem = posicaoDe(id)
    const inicioX = e.clientX
    const inicioY = e.clientY
    let atual: iPosicaoTopo | undefined

    const mover = (ev: PointerEvent): void => {
      atual = {
        x: Math.max(0, Math.round(origem.x + (ev.clientX - inicioX) / zoom)),
        y: Math.max(0, Math.round(origem.y + (ev.clientY - inicioY) / zoom))
      }
      setArraste({ id, ...atual })
    }

    const soltar = (): void => {
      window.removeEventListener('pointermove', mover)
      window.removeEventListener('pointerup', soltar)
      setArraste(undefined)
      if (atual) data.onMoverTopo?.(data.departamentoId, id, atual)
    }

    window.addEventListener('pointermove', mover)
    window.addEventListener('pointerup', soltar)
  }

  function GetTree(Tree: IEmployee[], nivel: number, comConector = false): any {
    const corDoNivel = data.cores[nivel] ? data.cores[nivel] : 'var(--Azul)'
    const corDoNivelSeguinte = data.cores[nivel + 1] ? data.cores[nivel + 1] : 'var(--Azul)'

    return Tree.map((colab) => (
      <Flex
        key={colab.id}
        flexDir="column"
        align="center"
        position="relative"
        pt={comConector ? `${ALTURA_CONECTOR}px` : undefined}
        // a barra invade metade do espaçamento dos lados para emendar na do irmão
        _before={comConector
          ? {
              content: '""',
              position: 'absolute',
              top: METADE_DO_CONECTOR,
              left: '-4px',
              right: '-4px',
              height: '2px',
              background: corDoNivel
            }
          : undefined}
        _after={comConector
          ? {
              content: '""',
              position: 'absolute',
              top: METADE_DO_CONECTOR,
              left: '50%',
              width: '2px',
              height: METADE_DO_CONECTOR,
              transform: 'translateX(-50%)',
              background: corDoNivel
            }
          : undefined}
      >
        <Flex
          {...(data.onMoverTopo && nivel === data.nivel && {
            className: 'nodrag nopan',
            cursor: 'grab',
            onPointerDown: (e: React.PointerEvent) => iniciarArraste(e, colab.id)
          })}
          flexDir="column"
          border={`1px solid ${data.cores[nivel] ? data.cores[nivel] : 'var(--Azul)'}`}
          pt="0.5rem"
          bg="var(--c1)"
          w="fit-content"
          minW="120px"
          maxW="120px"
          height="9rem"
          alignItems="center"
          justifyContent="space-between"
          borderRadius="0.25rem"
        >
          <Flex justifyContent="center">
            <Flex flexDir="column" alignItems="center">
              <Flex justifyContent="center" height="5rem" w="5rem">
                <Image
                  w="full"
                  h="full"
                  objectFit="cover"
                  borderRadius="50%"
                  src={
                    colab.avatar ||
                    'https://sigah.blob.core.windows.net/assets/user.png'
                    }
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex w="full" justifyContent="center">
            <Text
              bg={data.cores[nivel] ? data.cores[nivel] : 'var(--Azul)'}
              width="full"
              textAlign="center"
              color="var(--c1)"
              p="0.25rem"
              fontWeight="600"
              fontSize="0.65rem"
              noOfLines={colab.papelSquad ? 3 : 2}
            >
              {colab.nome} {colab.sobrenome}
              {colab.papelSquad && <Text as="span" display="block" fontWeight="400">{colab.papelSquad}</Text>}
            </Text>
          </Flex>
        </Flex>
        {colab.subordinados && (
          <Flex
            gap="0.5rem"
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              width: '2px',
              height: METADE_DO_CONECTOR,
              transform: 'translateX(-50%)',
              background: corDoNivelSeguinte
            }}
            sx={{
              '& > div:first-of-type::before': { left: '50%' },
              '& > div:last-of-type::before': { right: '50%' },
              '& > div:only-of-type::before': { display: 'none' }
            }}
          >
            {GetTree(colab.subordinados, nivel + 1, true)}
          </Flex>
        )}
      </Flex>
    ))
  }

  return (
    <Box
      border={`1px solid ${data.cor ?? 'var(--Azul)'}`}
      borderRadius="0.25rem"
      bg="white"
      p="0.75rem"
      display="flex"
      flexDir="column"
      overflowX="auto"
      height="100%"
      alignItems="center"
      minW="240px"
      width={'100%'}
    >
      <Text
        fontWeight="900"
        mb="0.5rem"
        textAlign="center"
        fontSize="1.125rem"
        whiteSpace="normal"
        wordBreak="break-word"
        color={data.cor}
      >
        {data.label}
      </Text>

      {data.colaboradores?.length > 0 && data.onMoverTopo && (
        <Box position="relative" w="100%" minH={`${Math.max(...data.colaboradores.map(colab => posicaoDe(colab.id).y + medirTopo(colab).altura))}px`}>
          {data.colaboradores.map(colab => {
            const posicao = Arraste?.id === colab.id ? Arraste : posicaoDe(colab.id)
            return (
              <Box key={colab.id} position="absolute" left={`${posicao.x}px`} top={`${posicao.y}px`} w={`${medirTopo(colab).largura}px`} zIndex={Arraste?.id === colab.id ? 1 : undefined}>
                {GetTree([colab], data.nivel)}
              </Box>
            )
          })}
        </Box>
      )}

      {data.colaboradores?.length > 0 && !data.onMoverTopo && (
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="flex-start"
          gap="0.5rem"
          w="100%"
          justifyContent="center"
          py="0.5rem"
        >
          {GetTree(data.colaboradores, data.nivel)}
        </Box>
      )}

      <NodeResizer
          minWidth={240}
          minHeight={120}
          autoScale
          color={data.cor ?? 'var(--Azul)'}
          handleStyle={{
            width: 8,
            height: 8,
            background: `${data.cor ?? 'var(--Azul)'}`,
            border: `2px solid ${data.cor ?? 'var(--Azul)'}`,
            marginTop: '4px',
            marginLeft: '4px'
          }}
          lineStyle={{
            stroke: data.cor ?? 'var(--Azul)',
            strokeWidth: 2
          }}
        />
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} style={{ visibility: visibilidadeAlca }} />
      <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} style={{ visibility: visibilidadeAlca }} />
    </Box>
  )
}

export default memo(CustomNode)
